import { inject, Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AttachmentEditModalComponent } from '../components/attachments/attachment-edit-modal/attachment-edit-modal.component';
import { AttachmentUploadDetailsModalComponent } from '../components/attachments/attachment-upload-details-modal/attachment-upload-details-modal.component';
import { AttachmentUploadFailureModalComponent } from '../components/attachments/attachment-upload-failure-modal/attachment-upload-failure-modal.component';
import { AttachmentUploadModalComponent } from '../components/attachments/attachment-upload-modal/attachment-upload-modal.component';
import { AttachmentUploadSuccessModalComponent } from '../components/attachments/attachment-upload-success-modal/attachment-upload-success-modal.component';
import { AccountCreditRequestModalComponent } from '../components/account-credit-request-modal/account-credit-request-modal.component';
import { ChangeOrderHistoryModalComponent } from '../components/change-order-history-modal/change-order-history-modal.component';
import { ChangeOrderModalComponent, TaxEditLine } from '../components/change-order-modal/change-order-modal.component';
import { CommissionEditModalComponent } from '../components/commission-edit-modal/commission-edit-modal.component';
import { CommissionPaymentModalComponent } from '../components/commission-payment-modal/commission-payment-modal.component';
import { ConfirmModalComponent } from '../components/confirm-modal/confirm-modal.component';
import { ErrorStateModalComponent } from '../components/error-state-modal/error-state-modal.component';
import { InfoModalComponent } from '../components/info-modal/info-modal.component';
import { ForemanConfigurationModalComponent } from '../components/foreman-configuration-modal/foreman-configuration-modal.component';
import { ForemanDetailModalComponent } from '../components/foreman-detail-modal/foreman-detail-modal.component';
import { JobCancelModalComponent } from '../components/job-cancel-modal/job-cancel-modal.component';
import { PaymentEditTarget, PostPaymentModalComponent } from '../components/post-payment-modal/post-payment-modal.component';
import { PaymentHistoryModalComponent } from '../components/payment-history-modal/payment-history-modal.component';
import { ProjectCompletionModalComponent } from '../components/project-completion-modal/project-completion-modal.component';
import { RemoveAvailabilityModalComponent } from '../components/remove-availability-modal/remove-availability-modal.component';
import { ScheduleSuccessModalComponent } from '../components/schedule-success-modal/schedule-success-modal.component';
import { SpecialNeedModalComponent } from '../components/special-need-modal/special-need-modal.component';
import { SplitProjectModalComponent } from '../components/split-project-modal/split-project-modal.component';
import { PermitModalComponent } from '../components/permit-modal/permit-modal.component';
import { PpjExceptionModalComponent } from '../components/ppj-exception-modal/ppj-exception-modal.component';
import { PpjForemanBonusModalComponent } from '../components/ppj-foreman-bonus-modal/ppj-foreman-bonus-modal.component';
import { UtilityModalComponent } from '../components/utility-modal/utility-modal.component';
import {
  BoundProductInfo,
  CommissionResponse,
  CommissionWithSummary,
  ContactInfo,
  PaymentDetailsBilling,
  PendingCreditRequestInfo,
  PpjExceptionResponse,
  PpjPaymentResponse,
  TaxInfo,
} from '../sdk/sdk';
import { SendStatementModalComponent } from '../components/send-statement-modal/send-statement-modal.component';


@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private readonly ngbModal = inject(NgbModal);

  openErrorModal(title: string, message: string): NgbModalRef {
    const modalRef = this.ngbModal.open(ErrorStateModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.title.set(title);
    modalRef.componentInstance.alertMessage.set(message);

    return modalRef;
  }

  openConfirmModal(
    title: string,
    message: string,
    confirmButtonText = 'Confirm',
    alertClasses = 'alert-warning',
    showCancelButton = true,
    confirmButtonClass = 'btn-danger',
    alertIcon = '',
    cancelButtonText = 'Cancel',
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(ConfirmModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.title.set(title);
    modalRef.componentInstance.message.set(message);
    modalRef.componentInstance.confirmButtonText.set(confirmButtonText);
    modalRef.componentInstance.alertClasses.set(alertClasses);
    modalRef.componentInstance.showCancelButton.set(showCancelButton);
    modalRef.componentInstance.confirmButtonClass.set(confirmButtonClass);
    modalRef.componentInstance.alertIcon.set(alertIcon);
    modalRef.componentInstance.cancelButtonText.set(cancelButtonText);

    return modalRef;
  }

  openAttachmentEditModal(
    attachmentId: string,
    initialGroupId?: string,
    initialDescription?: string
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(AttachmentEditModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.attachmentId.set(attachmentId);
    modalRef.componentInstance.initialGroupId.set(initialGroupId);
    modalRef.componentInstance.initialDescription.set(
      initialDescription ?? ''
    );

    return modalRef;
  }

  openAttachmentUploadModal(): NgbModalRef {
    return this.ngbModal.open(AttachmentUploadModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });
  }

  openAttachmentUploadDetailsModal(
    files: File[],
    opportunityId: string
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(AttachmentUploadDetailsModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.files = files;
    modalRef.componentInstance.opportunityId = opportunityId;

    return modalRef;
  }

  openAttachmentUploadFailureModal(
    failedFiles: File[],
    opportunityId: string,
    groupId: string | null,
    description: string,
    sectionId: string | null
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(
      AttachmentUploadFailureModalComponent,
      {
        backdrop: 'static',
        keyboard: false,
        centered: true,
      }
    );

    modalRef.componentInstance.failedFiles = failedFiles;
    modalRef.componentInstance.opportunityId = opportunityId;
    modalRef.componentInstance.groupId = groupId;
    modalRef.componentInstance.description = description;
    modalRef.componentInstance.sectionId = sectionId;

    return modalRef;
  }

  openAttachmentUploadSuccessModal(): NgbModalRef {
    return this.ngbModal.open(AttachmentUploadSuccessModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });
  }

  openChangeOrderModal(
    jobId: string,
    currentJobTotal: number,
    branchId: string | undefined,
    postalCode: string | undefined,
    taxes: TaxEditLine[] = []
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(ChangeOrderModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'lg',
    });

    modalRef.componentInstance.jobId.set(jobId);
    modalRef.componentInstance.jobNumber.set(jobId);
    modalRef.componentInstance.currentJobTotal.set(currentJobTotal);
    if (branchId) modalRef.componentInstance.branchId.set(branchId);
    if (postalCode) modalRef.componentInstance.postalCode.set(postalCode);
    modalRef.componentInstance.taxes.set(taxes);

    return modalRef;
  }

  openSplitProjectModal(
    projectId: string,
    boundProducts: BoundProductInfo[],
    taxes: TaxInfo[],
    categoryByBoundProductId: ReadonlyMap<string, string>
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(SplitProjectModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      // The side-by-side Current/New tables need ~1400px; ng-bootstrap's widest stock size is
      // 'xl', so a windowClass widens the dialog via a global rule in styles.scss.
      size: 'xl',
      windowClass: 'split-project-modal',
    });

    modalRef.componentInstance.projectId.set(projectId);
    modalRef.componentInstance.boundProducts.set(boundProducts);
    modalRef.componentInstance.taxes.set(taxes);
    modalRef.componentInstance.categoryByBoundProductId.set(categoryByBoundProductId);

    return modalRef;
  }

  openChangeOrderHistoryModal(jobId: string): NgbModalRef {
    const modalRef = this.ngbModal.open(ChangeOrderHistoryModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'lg',
    });

    modalRef.componentInstance.jobId.set(jobId);

    return modalRef;
  }

  openPaymentHistoryModal(opportunityId: string): NgbModalRef {
    const modalRef = this.ngbModal.open(PaymentHistoryModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'lg',
      scrollable: true,
    });

    modalRef.componentInstance.opportunityId.set(opportunityId);

    return modalRef;
  }

  openPostPaymentModal(
    opportunityId: string,
    jobId: string | null,
    bcJobNumber: string | null = null
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(PostPaymentModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.opportunityId.set(opportunityId);
    modalRef.componentInstance.jobId.set(jobId);
    modalRef.componentInstance.bcJobNumber.set(bcJobNumber);

    return modalRef;
  }

  openEditPaymentModal(
    opportunityId: string,
    jobId: string | null,
    target: PaymentEditTarget
  ): NgbModalRef {
    const modalRef = this.openPostPaymentModal(opportunityId, jobId);
    modalRef.componentInstance.setEditTarget(target);
    return modalRef;
  }

  openAccountCreditRequestModal(
    opportunityId: string,
    jobId: string | null,
    bcJobNumber: string | null = null
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(AccountCreditRequestModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.opportunityId.set(opportunityId);
    modalRef.componentInstance.jobId.set(jobId);
    modalRef.componentInstance.bcJobNumber.set(bcJobNumber);

    return modalRef;
  }

  openReviewAccountCreditRequestModal(
    opportunityId: string,
    jobId: string | null,
    bcJobNumber: string | null,
    pendingCreditRequests: PendingCreditRequestInfo[]
  ): NgbModalRef {
    const modalRef = this.openAccountCreditRequestModal(opportunityId, jobId, bcJobNumber);
    modalRef.componentInstance.reviewMode.set(true);
    modalRef.componentInstance.setPendingCreditRequests(pendingCreditRequests);
    return modalRef;
  }

  openAcrSuccessModal(creditAmount: number, bcJobNumber: string | null): NgbModalRef {
    const message = `An Account Credit Request of ${ModalService.formatUsd(creditAmount)} was successfully requested for ${ModalService.jobLabel(bcJobNumber)}.`;
    return this.openSuccessModal('Account Credit Request', message);
  }

  openAcrDeleteSuccessModal(creditAmount: number, bcJobNumber: string | null): NgbModalRef {
    const message = `The Account Credit Request of ${ModalService.formatUsd(creditAmount)} for ${ModalService.jobLabel(bcJobNumber)} was successfully deleted.`;
    return this.openSuccessModal('Account Credit Request', message);
  }

  openAcrEditSuccessModal(creditAmount: number, bcJobNumber: string | null): NgbModalRef {
    const message = `The Account Credit Request of ${ModalService.formatUsd(creditAmount)} for ${ModalService.jobLabel(bcJobNumber)} was successfully updated.`;
    return this.openSuccessModal('Account Credit Request', message);
  }

  /**
   * Confirmation for approving a refund-type ACR. Approval alone creates no payment — the refund
   * goes to Accounting, who post it on the day they actually issue it — so this must not reuse
   * `openPaymentSuccessModal`, which tells the approver a payment was posted.
   */
  openAcrAwaitingPostModal(creditAmount: number, bcJobNumber: string | null): NgbModalRef {
    const message = `An Account Credit Request of ${ModalService.formatUsd(creditAmount)} for ${ModalService.jobLabel(bcJobNumber)} was approved and sent to Accounting to post. No payment has been recorded yet.`;
    return this.openSuccessModal('Account Credit Request', message);
  }

  openPaymentSuccessModal(paymentAmount: number, bcJobNumber: string | null): NgbModalRef {
    const message = `A payment of ${ModalService.formatUsd(paymentAmount)} was successfully posted to ${ModalService.jobLabel(bcJobNumber)}.`;
    return this.openSuccessModal('Payment', message);
  }

  private static formatUsd(amount: number): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  }

  private static jobLabel(bcJobNumber: string | null): string {
    return bcJobNumber ?? '—';
  }

  private openSuccessModal(title: string, message: string): NgbModalRef {
    return this.openInfoModal(title, message, 'bi bi-check-circle-fill');
  }

  openJobCancelModal(
    title: string,
    jobId: string,
    alertClasses = 'alert-danger'
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(JobCancelModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });
    modalRef.componentInstance.title.set(title);
    modalRef.componentInstance.jobId.set(jobId);
    modalRef.componentInstance.alertClasses.set(alertClasses);
    return modalRef;
  }

  openInfoModal(
    title: string,
    message: string,
    icon: string,
    alertClasses = 'alert-success border-success'
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(InfoModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });
    modalRef.componentInstance.title.set(title);
    modalRef.componentInstance.message.set(message);
    modalRef.componentInstance.icon.set(icon);
    modalRef.componentInstance.alertClasses.set(alertClasses);
    return modalRef;
  }

  openJobCancelSuccessModal(
    alertClasses = 'alert-success border-success'
  ): NgbModalRef {
    return this.openInfoModal(
      'Cancel Job',
      'Job successfully canceled. It will be automatically removed from the schedule.',
      'bi bi-check-circle-fill',
      alertClasses
    );
  }

  openForemanDetailModal(foremanUserId: string, schedulingMode = false): NgbModalRef {
    const modalRef = this.ngbModal.open(ForemanDetailModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.foremanUserId.set(foremanUserId);
    modalRef.componentInstance.schedulingMode.set(schedulingMode);

    return modalRef;
  }

  openForemanConfigModal(foremanUserId: string): NgbModalRef {
    const modalRef = this.ngbModal.open(ForemanConfigurationModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'lg',
    });

    modalRef.componentInstance.foremanUserId.set(foremanUserId);

    return modalRef;
  }

  openRemoveAvailabilityModal(reasons: string[]): NgbModalRef {
    const modalRef = this.ngbModal.open(RemoveAvailabilityModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.reasons.set(reasons);

    return modalRef;
  }

  openProjectCompletionModal(
    projectId: string,
    projectTypeName?: string,
    splitContext?: { projectTypeId?: string; boundProducts: BoundProductInfo[] }
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(ProjectCompletionModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.projectId.set(projectId);
    modalRef.componentInstance.projectTypeName.set(projectTypeName);
    if (splitContext) {
      modalRef.componentInstance.projectTypeId.set(splitContext.projectTypeId);
      modalRef.componentInstance.boundProducts.set(splitContext.boundProducts);
    }

    return modalRef;
  }

  openScheduleSuccessModal(params: {
    projectTypeName: string;
    remainingProjectCount: number;
  }): NgbModalRef {
    const modalRef = this.ngbModal.open(ScheduleSuccessModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.projectTypeName.set(params.projectTypeName);
    modalRef.componentInstance.remainingProjectCount.set(params.remainingProjectCount);

    return modalRef;
  }

  openSpecialNeedEditModal(projectId: string, branchId: string, specialNeed: unknown): NgbModalRef {
    const modalRef = this.ngbModal.open(SpecialNeedModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.projectId.set(projectId);
    modalRef.componentInstance.branchId.set(branchId);
    modalRef.componentInstance.specialNeed.set(specialNeed);
    modalRef.componentInstance.loadNeed();
    modalRef.componentInstance.loadUsers();

    return modalRef;
  }

  openSpecialNeedModal(projectId: string, branchId: string): NgbModalRef {
    const modalRef = this.ngbModal.open(SpecialNeedModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.projectId.set(projectId);
    modalRef.componentInstance.branchId.set(branchId);
    modalRef.componentInstance.loadUsers();

    return modalRef;
  }

  openUtilityModal(projectId: string): NgbModalRef {
    const modalRef = this.ngbModal.open(UtilityModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.projectId.set(projectId);

    return modalRef;
  }

  openPermitEditModal(projectId: string, permitId: string, branchId: string): NgbModalRef {
    const modalRef = this.ngbModal.open(PermitModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'xl',
    });

    modalRef.componentInstance.projectId.set(projectId);
    modalRef.componentInstance.permitId.set(permitId);
    modalRef.componentInstance.branchId.set(branchId);
    modalRef.componentInstance.loadPermit();
    // Phase 3 EoR dropdown needs the engineers-of-record list regardless of mode.
    modalRef.componentInstance.loadEngineersOfRecord();

    return modalRef;
  }

  openPermitCreateModal(projectId: string, branchId: string): NgbModalRef {
    // Create flow: only projectId is set, no permitId / loadPermit. The modal stays in
    // isEditMode=false, shows the "Add Permit: Program Details" title, and routes save()
    // to apiClient.createPermit.
    const modalRef = this.ngbModal.open(PermitModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'xl',
    });

    modalRef.componentInstance.projectId.set(projectId);
    modalRef.componentInstance.branchId.set(branchId);
    // EoR list is needed even in create mode so the user can pre-select before navigating
    // to Phase 3 (though Save on Phase 3 is gated until the permit is created on Phase 1).
    modalRef.componentInstance.loadEngineersOfRecord();

    return modalRef;
  }

  openSendStatementModal(
    contacts: ContactInfo[],
    opportunityId: string | null,
    billing: PaymentDetailsBilling | null,
  ): NgbModalRef {
    // scrollable: the body can hold the picker, two totals, the Split Payment control and a
    // validation message, which outgrows a short viewport and would otherwise push the footer
    // (and Send) off-screen. Same option the payment-history modal uses.
    const modalRef = this.ngbModal.open(SendStatementModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      scrollable: true,
    });
    modalRef.componentInstance.contacts.set(contacts);
    modalRef.componentInstance.opportunityId.set(opportunityId);
    modalRef.componentInstance.billing.set(billing);
    return modalRef;
  }

  openUtilityEditModal(projectId: string, utility: unknown): NgbModalRef {
    const modalRef = this.ngbModal.open(UtilityModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.projectId.set(projectId);
    modalRef.componentInstance.utility.set(utility);
    modalRef.componentInstance.loadUtility();

    return modalRef;
  }

  openCommissionPaymentModal(commission: CommissionWithSummary): NgbModalRef {
    const modalRef = this.ngbModal.open(CommissionPaymentModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.commission.set(commission);

    return modalRef;
  }

  openCommissionEditModal(
    projectId: string,
    branchId: string | null,
    commission: CommissionResponse | null,
    isSelfGen: boolean,
    sellerName: string | null = null,
    adjustedSale: number | null = null,
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(CommissionEditModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.projectId.set(projectId);
    modalRef.componentInstance.branchId.set(branchId);
    modalRef.componentInstance.commission.set(commission);
    modalRef.componentInstance.isSelfGen.set(isSelfGen);
    modalRef.componentInstance.sellerName.set(sellerName);
    modalRef.componentInstance.adjustedSale.set(adjustedSale);

    return modalRef;
  }

  openPpjForemanBonusModal(
    projectId: string,
    userId: string,
    bonusPot: number | null,
    payment: PpjPaymentResponse | null,
    defaultBonusPercent: number | null
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(PpjForemanBonusModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.projectId.set(projectId);
    modalRef.componentInstance.userId.set(userId);
    modalRef.componentInstance.bonusPot.set(bonusPot);
    modalRef.componentInstance.payment.set(payment);
    modalRef.componentInstance.defaultBonusPercent.set(defaultBonusPercent);

    return modalRef;
  }

  // `exception` null adds, non-null edits — the modal reads the difference off that one signal.
  openPpjExceptionModal(projectId: string, exception: PpjExceptionResponse | null): NgbModalRef {
    const modalRef = this.ngbModal.open(PpjExceptionModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.projectId.set(projectId);
    modalRef.componentInstance.exception.set(exception);

    return modalRef;
  }
}
